import { useCallback } from 'react'
import { normalizeMessages } from '@/features/chat/messageNormalize'
import { buildUserAndPendingMessages } from './helpers/buildUserAndPendingMessages'
import { createThreadAbortController } from './helpers/threadAbortController'
import { priorMessagesWithoutPending } from './helpers/priorMessagesWithoutPending'
import { regenerateInstruction } from './helpers/regenerateInstruction'
import { scheduleCompletionUpdate } from './helpers/scheduleCompletionUpdate'

/** @param {object} m @param {ReturnType<typeof import('./useExchangeVersionActions').useExchangeVersionActions>} v */
export function useRegenerateAssistantAction(m, v) {
  const { messagesByThreadId, chatAbortRef, setMessagesByThreadId } = m
  const { getNextAssistantVersionIndex, setExchangeActiveAnswerVersion } = v
  const handleRegenerateAssistantVersion = useCallback(
    ({ threadId, exchangeId, versionIndex, mode }) => {
      const tid = threadId
      chatAbortRef.current?.abort()
      const controller = createThreadAbortController(tid)
      chatAbortRef.current = controller
      const { signal } = controller
      const list = normalizeMessages(messagesByThreadId[tid] ?? [])
      const baseUser = list
        .filter((msg) => msg.role === 'user' && msg.exchangeId === exchangeId && msg.versionIndex === versionIndex)
        .sort((a, b) => (a.createdAt ?? 0) - (b.createdAt ?? 0))[0]
      const baseText = String(baseUser?.text ?? '').trim()
      if (!baseText) return
      const nextAssistantIndex = getNextAssistantVersionIndex(tid, exchangeId, versionIndex)
      const instruction = regenerateInstruction(mode)
      setMessagesByThreadId((prev) => {
        const priorMessages = priorMessagesWithoutPending(prev, tid)
        const now = Date.now()
        const { pendingId, pendingAssistant } = buildUserAndPendingMessages({
          trimmed: '',
          exchangeId,
          versionIndex,
          assistantVersionIndex: nextAssistantIndex,
          now,
        })
        const apiMessages = [
          ...priorMessages.map((msg) => ({ role: msg.role, content: msg.text })),
          { role: 'user', content: `${instruction}\n\nQuestion:\n${baseText}` },
        ]
        scheduleCompletionUpdate({ tid, pendingId, signal, setMessagesByThreadId, apiMessages, mode })
        return { ...prev, [tid]: [...priorMessages, pendingAssistant] }
      })
      setExchangeActiveAnswerVersion(exchangeId, nextAssistantIndex)
    },
    [getNextAssistantVersionIndex, messagesByThreadId, setExchangeActiveAnswerVersion, chatAbortRef, setMessagesByThreadId],
  )
  return { handleRegenerateAssistantVersion }
}
